import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ModernLogo } from "@/components/ModernLogo";
import { ContactSupportDialog } from "@/components/ContactSupportDialog";
import { 
  CheckCircle, 
  XCircle, 
  Percent, 
  Clock, 
  Shield, 
  Star,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Zap,
  TrendingUp,
  Users,
  Calendar,
  CreditCard,
  Award,
  Wrench,
  Home
} from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { useIsMobile } from "@/hooks/use-mobile";

export function ForTradesmen() {
  const [showContactDialog, setShowContactDialog] = useState(false);
  const isMobile = useIsMobile();

  const benefits = [
    {
      icon: <CreditCard className="w-6 h-6 text-primary" />,
      title: "No Monthly Contracts",
      description: "Complete freedom - no lock-in periods, no recurring fees. Join and leave whenever you want."
    },
    {
      icon: <XCircle className="w-6 h-6 text-primary" />,
      title: "No Payment for Declined Jobs",
      description: "Only pay when you accept and complete a job. Browse opportunities at no cost."
    },
    {
      icon: <Percent className="w-6 h-6 text-primary" />,
      title: "Just 7% Commission",
      description: "Industry-leading low commission rate. Keep 93% of every job's agreed price."
    },
    {
      icon: <Zap className="w-6 h-6 text-primary" />,
      title: "Quality Leads",
      description: "Customers post jobs and are actively comparing quotes - no time wasters."
    },
    {
      icon: <Shield className="w-6 h-6 text-primary" />,
      title: "Verified Customers",
      description: "All customers are vetted and payment is secured before work begins."
    },
    {
      icon: <Award className="w-6 h-6 text-primary" />,
      title: "Build Your Reputation",
      description: "Customer reviews and ratings help you build trust and attract more work."
    }
  ];

  const competitors = [
    {
      name: "MyBuilder",
      monthlyFee: "Unlimited",
      commission: "£0 (but unlimited monthly fee)",
      jobCost: "Must pay unlimited regardless of work",
      our: false
    },
    {
      name: "Checkatrade",
      monthlyFee: "£150+/month",
      commission: "£0 (but very high monthly fee)",
      jobCost: "Expensive monthly subscription",
      our: false
    },
    {
      name: "TaskRabbit",
      monthlyFee: "£0",
      commission: "15% commission",
      jobCost: "High commission on every job",
      our: false
    },
    {
      name: "Our Platform",
      monthlyFee: "£0",
      commission: "7% commission",
      jobCost: "Only pay when you work",
      our: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="For Tradesmen - Join Sortable | No Monthly Fees"
        description="Join verified UK tradesmen on Sortable. Grow your business with quality leads, low 7% commission, and no monthly fees or lock-in contracts."
        keywords="tradesperson leads, get more work, join Sortable, sign up as a tradesperson, tradesperson membership, generate more leads, grow my trade business, list my business for free, tradesperson registration, Sortable for tradespeople, lead generation for trades, find local customers, marketing for tradespeople, how Sortable works for trades, tradesperson app, manage my jobs, receive customer enquiries, verified tradesperson profile, tradesperson pricing, Sortable pro membership, tradesperson success stories, connect with homeowners, join tradesmen network, tradesman app, contractor platform, grow trade business, get more customers, tradesman jobs, contractor leads, skilled trade work, professional networking, trade business growth, customer acquisition, job opportunities, trade marketplace, contractor directory, skilled worker platform, trade professional registration, business expansion, recurring customers, trade certification, professional development, contractor insurance, trade licensing, business management tools, invoice generation, customer communication, job scheduling, payment processing, trade portfolio, customer reviews, business analytics, mobile app for tradesmen, field service management, work order system, quote generation, project management, trade specialization, subcontractor network, partnership opportunities, revenue growth, client retention, no monthly fees, low commission, fair pricing, tradesman earnings, contractor income, trade business model, flexible working, self employed, freelance tradesman, independent contractor, work life balance, career opportunities"
        canonical="https://sortable.uk/for-tradesmen"
        ogImage="https://sortable.uk/hero-tradesman.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "For Tradesmen - Join Sortable",
          "description": "Join thousands of verified tradesmen on Sortable platform",
          "url": "https://sortable.uk/for-tradesmen"
        }}
      />
      {/* Modern Header */}
      <header className="relative bg-gradient-to-r from-background via-background/95 to-background backdrop-blur-lg border-b border-border/50 shadow-elegant sticky top-0 z-50">
        <div className="absolute inset-0 bg-gradient-hero opacity-5"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex justify-between items-center ${isMobile ? 'py-2' : 'py-4'}`}>
            {/* Brand Section */}
            <div className="flex items-center relative z-20">
              <Link to="/">
                <ModernLogo size="sm" showTagline={false} />
              </Link>
            </div>

            {/* Navigation & Action Buttons */}
            <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-3'}`}>
              <Link to="/why-us" className={isMobile ? 'hidden' : ''}>
                <Button variant="outline" size={isMobile ? "sm" : "default"} className="shadow-sm hover:shadow-md transition-all duration-200">
                  <Shield className={`${isMobile ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-2'}`} />
                  {isMobile ? 'Why Us' : 'Why Choose Us'}
                </Button>
              </Link>
              <Link to="/" className={isMobile ? 'hidden' : ''}>
                <Button variant="outline" size={isMobile ? "sm" : "default"} className="shadow-sm hover:shadow-md transition-all duration-200">
                  <Home className={`${isMobile ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-2'}`} />
                  {isMobile ? 'Home' : 'For Customers'}
                </Button>
              </Link>
              <Link to="/tradesman-registration">
                <Button variant="hero" size={isMobile ? "sm" : "lg"} className={`shadow-glow hover:shadow-elegant transition-all duration-300 font-semibold ${isMobile ? 'px-3 py-1.5 text-sm' : ''}`}>
                  {isMobile ? 'Join' : 'Join Free'}
                </Button>
              </Link>
              {isMobile && <>
                  <Link to="/why-us">
                    <Button variant="outline" size="sm" className="shadow-sm hover:shadow-md transition-all duration-200 px-2">
                      <Shield className="h-3 w-3" />
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" size="sm" className="shadow-sm hover:shadow-md transition-all duration-200 px-2">
                      <Home className="h-3 w-3" />
                    </Button>
                  </Link>
                </>}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Enhanced for Conversion */}
      <section className="relative py-16 lg:py-24 bg-gradient-hero overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-hover to-primary opacity-95"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center text-white">
            <div className="inline-flex items-center gap-2 bg-accent-orange backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm font-semibold mb-6 animate-pulse shadow-orange">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
              </span>
              2,500+ Active Tradesmen Earning More
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
              Get More Jobs.
              <span className="text-accent-orange block mt-2">Keep 93% of Your Earnings</span>
            </h1>
            
            <p className="text-2xl mb-8 leading-relaxed text-white/95 max-w-3xl mx-auto">
              Join the platform built by tradesmen, for tradesmen. <span className="font-bold text-accent-orange">No monthly fees. No contracts.</span> Just quality leads from customers ready to hire.
            </p>
            
            <div className="flex flex-col gap-4 mb-8 max-w-md mx-auto">
              <Link to="/tradesman-registration">
                <Button className="w-full bg-accent-orange hover:bg-accent-orange-hover text-white shadow-orange text-xl font-bold py-8 group relative overflow-hidden">
                  <span className="relative z-10 flex items-center gap-3 justify-center">
                    <Wrench className="h-6 w-6" />
                    Join Free - Start Earning
                    <span className="ml-2">→</span>
                  </span>
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform"></div>
                </Button>
              </Link>
              
              <Button variant="outline-on-gradient" size="lg" asChild className="text-lg">
                <a href="tel:08000438880">
                  <Phone className="mr-2 h-5 w-5" />
                  Call 0800 043 8880
                </a>
              </Button>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 text-sm justify-center">
                <div className="flex items-center gap-2">
                  <div className="bg-green-500 rounded-full p-1">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <span>No Monthly Fees</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-green-500 rounded-full p-1">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <span>Only 7% Commission</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-green-500 rounded-full p-1">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <span>Verified Customers</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4 pt-6 border-t border-white/20 justify-center">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="w-12 h-12 rounded-full bg-accent-orange border-3 border-primary flex items-center justify-center text-white font-bold shadow-lg">
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
              </div>
              <div className="text-left">
                <div className="flex gap-1 mb-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <span key={i} className="text-yellow-400 text-lg">★</span>
                  ))}
                </div>
                <p className="text-sm"><span className="font-bold text-lg">4.8/5</span> from 2,500+ tradesmen</p>
              </div>
            </div>
            
            <div className="mt-8">
              <Link to="/why-us">
                <Button variant="link" className="text-white hover:text-accent-orange font-semibold text-lg">
                  <Shield className="h-5 w-5 mr-2" />
                  See Our Full Protection Guarantees →
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Urgency Bar */}
      <section className="bg-accent-orange text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-6 w-6 animate-pulse" />
              <p className="font-bold text-lg">Save £1,800/year compared to other platforms!</p>
            </div>
            <Link to="/tradesman-registration">
              <Button variant="outline-on-gradient" size="lg">
                Join Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Tradesmen Choose Us
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
              We've designed our platform with your success in mind. Here's what makes us different.
            </p>
            <Link to="/why-us">
              <Button variant="outline" className="shadow-sm hover:shadow-md">
                <Shield className="h-4 w-4 mr-2" />
                See All Benefits & Guarantees
              </Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-border/50 hover:shadow-elegant transition-all duration-300 hover:scale-105">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    {benefit.icon}
                    <CardTitle className="text-lg">{benefit.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {benefit.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Compare and Save Thousands
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how much you could save compared to other platforms.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-background rounded-lg border border-border/50 shadow-elegant">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left p-4 font-semibold">Platform</th>
                  <th className="text-left p-4 font-semibold">Monthly Fee</th>
                  <th className="text-left p-4 font-semibold">Commission</th>
                  <th className="text-left p-4 font-semibold">Cost Structure</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map((competitor, index) => (
                  <tr 
                    key={index} 
                    className={`border-b border-border/50 ${competitor.our ? 'bg-primary/5' : ''}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${competitor.our ? 'text-primary' : ''}`}>
                          {competitor.name}
                        </span>
                        {competitor.our && (
                          <Badge variant="default" className="text-xs">Recommended</Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={competitor.monthlyFee === '£0' ? 'text-green-600 font-medium' : 'text-destructive'}>
                        {competitor.monthlyFee}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={competitor.commission === '7% commission' ? 'text-green-600 font-medium' : ''}>
                        {competitor.commission}
                      </span>
                    </td>
                    <td className="p-4 text-muted-foreground">
                      {competitor.jobCost}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 p-6 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-start gap-4">
              <TrendingUp className="w-6 h-6 text-primary mt-1" />
              <div>
                <h3 className="font-semibold mb-2">Real Savings Example</h3>
                <p className="text-muted-foreground">
                  A tradesman earning £2,000/month would pay: <br />
                  • MyBuilder: Unlimited + 0% = Unlimited/year<br />
                  • Checkatrade: £150 + 0% = £1,800/year<br />
                  • TaskRabbit: £0 + 15% = £3,600/year<br />
                  • <strong className="text-primary">Our Platform: £0 + 7% = £1,680/year</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-lg text-muted-foreground">
              Simple setup, immediate access to quality jobs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "1",
                title: "Sign Up",
                description: "Create your profile and upload your insurance certificate",
                icon: <Users className="w-8 h-8 text-primary" />
              },
              {
                step: "2",
                title: "Get Verified",
                description: "Quick verification process to ensure customer trust",
                icon: <Shield className="w-8 h-8 text-primary" />
              },
              {
                step: "3",
                title: "Browse Jobs",
                description: "View local jobs that match your skills and availability",
                icon: <MapPin className="w-8 h-8 text-primary" />
              },
              {
                step: "4",
                title: "Get Paid",
                description: "Complete work and receive payment minus our 7% fee",
                icon: <CreditCard className="w-8 h-8 text-primary" />
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  {step.icon}
                </div>
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-3 text-sm font-bold">
                  {step.step}
                </div>
                <h3 className="font-semibold mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Join Thousands of Successful Tradesmen
            </h2>
            <div className="flex items-center justify-center gap-8 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <span>4.8/5 Average Rating</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                <span>2,500+ Active Tradesmen</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                <span>1,000+ Jobs Monthly</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                name: "Mike Thompson",
                trade: "Electrician",
                feedback: "No monthly fees means I can take breaks when I need to without losing money. The 7% fee is fair for the quality of customers.",
                rating: 5
              },
              {
                name: "Sarah Williams",
                trade: "Plumber",
                feedback: "Switched from Checkatrade and saved over £1,500 a year. The customers are genuine and payment is always secured.",
                rating: 5
              },
              {
                name: "David Chen",
                trade: "Carpenter",
                feedback: "Love that I only pay when I work. The platform brings me steady work without the hefty monthly subscriptions.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <Card key={index} className="border-border/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-500 fill-current" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-4 italic">
                    "{testimonial.feedback}"
                  </p>
                  <div>
                    <p className="font-semibold">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.trade}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 px-4 bg-gradient-hero overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-hover to-primary opacity-95"></div>
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Ready to Grow Your Business?
          </h2>
          <p className="text-2xl mb-8 leading-relaxed text-white/95">
            Join today and start receiving quality jobs with <span className="font-bold text-accent-orange">no upfront costs</span> or monthly commitments.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Link to="/tradesman-registration">
              <Button size="xl" className="bg-accent-orange hover:bg-accent-orange-hover text-white shadow-orange text-xl font-bold group relative overflow-hidden">
                <span className="relative z-10 flex items-center gap-3">
                  <Wrench className="h-6 w-6" />
                  Start Free Today
                  <ArrowRight className="h-5 w-5" />
                </span>
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform"></div>
              </Button>
            </Link>
            <Button size="xl" variant="outline-on-gradient" className="text-xl font-semibold" asChild>
              <a href="tel:08000438880">
                <Phone className="mr-2 h-6 w-6" />
                Speak to Our Team
              </a>
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 text-sm justify-center">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <span>Setup takes less than 10 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <span>Start earning immediately</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t border-border/50 py-12 px-4">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <ModernLogo showTagline={false} />
              <p className="text-muted-foreground">
                The platform that puts tradesmen first. No contracts, fair fees, quality customers.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Customers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/how-it-works-customer" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/about" className="hover:text-primary">About Us</Link></li>
                <li><Link to="/success-stories" className="hover:text-primary">Success Stories</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Tradesmen</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/how-it-works" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/pricing" className="hover:text-primary">Pricing</Link></li>
                <li><Link to="/for-tradesmen" className="hover:text-primary">Join Platform</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>08000438880</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>realise@post.com</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-border/50 mt-8 pt-8 text-center text-muted-foreground">
            <p>&copy; 2025 Sortable. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Contact Support Dialog */}
      <ContactSupportDialog 
        open={showContactDialog} 
        onOpenChange={setShowContactDialog} 
      />
    </div>
  );
}